
import { bookFacts, type BookCatalog } from "@/lib/book-catalog";
import { impliedProbability } from "@/lib/odds-chart-math";

export interface AvgSeriesPoint {
  t: number;
  american: number;
  removed?: boolean;
}

export interface AvgSeriesInput {
  book: string;
  contributes: boolean;
  points: AvgSeriesPoint[];
  earliestReturnedTs: number | null;
}

export type AvgGapReason = "truncated" | "insufficient";

export interface AvgValuePoint {
  t: number;
  gap: false;
  prob: number;
  american: number;
  n: number;
}

export interface AvgGapPoint {
  t: number;
  gap: true;
  reason: AvgGapReason;
}

export type AvgPoint = AvgValuePoint | AvgGapPoint;

export type AvgStatus = "ok" | "unavailable";

export interface AvgResult {
  status: AvgStatus;
  reason?: "absent-anchor";
  points: AvgPoint[];
  consensusCompleteFrom: number | null;
  maxContributors: number;
}

export function roundHalfAwayFromZero(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

export function probabilityToAmerican(p: number): number {
  if (p >= 0.5) {
    return -roundHalfAwayFromZero((100 * p) / (1 - p));
  }
  return roundHalfAwayFromZero((100 * (1 - p)) / p);
}

export function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

const EVENT_CONTRACT_EXCHANGES: ReadonlySet<string> = new Set(["kalshi", "polymarket", "cryptocom"]);

export type ConsensusBooks = { readonly contributors: ReadonlySet<string>; readonly unlisted: readonly string[] };

export function consensusContributors(catalog: BookCatalog | null, books: readonly string[]): ConsensusBooks {
  if (catalog === null) return { contributors: new Set(), unlisted: [] };
  const contributors = new Set<string>();
  const unlisted: string[] = [];
  for (const book of books) {
    const facts = bookFacts(catalog, book);
    if (facts === undefined) unlisted.push(book);
    else if ((facts.kind === "retail" || facts.kind === "exchange") && !EVENT_CONTRACT_EXCHANGES.has(facts.id)) contributors.add(book);
  }
  return { contributors: unlisted.length === 0 ? contributors : new Set(), unlisted };
}

function statesEqual(a: AvgPoint, b: AvgPoint): boolean {
  if (a.gap && b.gap) {
    return a.reason === b.reason;
  }
  if (!a.gap && !b.gap) {
    return a.american === b.american && a.n === b.n;
  }
  return false;
}

function isMandatoryBoundary(prev: AvgPoint, cur: AvgPoint): boolean {
  if (prev.gap !== cur.gap) {
    return true;
  }
  if (prev.gap && cur.gap) {
    return prev.reason !== cur.reason;
  }
  return !prev.gap && !cur.gap && prev.n !== cur.n;
}

function decimate(points: AvgPoint[], max: number): AvgPoint[] {
  if (points.length <= max) {
    return points;
  }
  const mandatory = new Set<number>([0, points.length - 1]);
  for (let i = 1; i < points.length; i += 1) {
    if (isMandatoryBoundary(points[i - 1], points[i])) {
      mandatory.add(i - 1);
      mandatory.add(i);
    }
  }
  if (mandatory.size >= max) {
    return Array.from(mandatory)
      .sort((a, b) => a - b)
      .map((i) => points[i]);
  }
  const ordinary: number[] = [];
  for (let i = 0; i < points.length; i += 1) {
    if (!mandatory.has(i)) {
      ordinary.push(i);
    }
  }
  const keep = new Set(mandatory);
  const budget = max - mandatory.size;
  if (ordinary.length <= budget) {
    for (const i of ordinary) {
      keep.add(i);
    }
  } else {
    const stride = ordinary.length / budget;
    for (let k = 0; k < budget; k += 1) {
      keep.add(ordinary[Math.floor(k * stride)]);
    }
  }
  return Array.from(keep)
    .sort((a, b) => a - b)
    .map((i) => points[i]);
}

export function computeConsensusSeries(
  inputs: AvgSeriesInput[],
  anchorBook: string,
  minContributors = 3,
  maxPoints = 500,
): AvgResult {
  const anchor = inputs.find((s) => s.book === anchorBook);
  if (!anchor || anchor.points.length === 0) {
    return {
      status: "unavailable",
      reason: "absent-anchor",
      points: [],
      consensusCompleteFrom: null,
      maxContributors: 0,
    };
  }
  const eligible = inputs.filter((s) => s.contributes);

  let consensusCompleteFrom: number | null = null;
  for (const s of eligible) {
    if (s.earliestReturnedTs != null) {
      consensusCompleteFrom =
        consensusCompleteFrom == null
          ? s.earliestReturnedTs
          : Math.max(consensusCompleteFrom, s.earliestReturnedTs);
    }
  }

  const tsSet = new Set<number>();
  for (const s of eligible) {
    for (const p of s.points) {
      tsSet.add(p.t);
    }
  }
  if (consensusCompleteFrom != null) {
    tsSet.add(consensusCompleteFrom);
  }
  const unionTs = Array.from(tsSet).sort((a, b) => a - b);

  let maxContributors = 0;
  const raw: AvgPoint[] = [];
  const cursors = eligible.map(() => -1);
  for (const t of unionTs) {
    if (consensusCompleteFrom != null && t < consensusCompleteFrom) {
      raw.push({ t, gap: true, reason: "truncated" });
      continue;
    }
    const probs: number[] = [];
    for (const [i, s] of eligible.entries()) {
      while (cursors[i] + 1 < s.points.length && s.points[cursors[i] + 1].t <= t) {
        cursors[i] += 1;
      }
      const sample = s.points[cursors[i]];
      if (sample && !sample.removed) {
        const prob = impliedProbability(sample.american);
        if (Number.isFinite(prob)) {
          probs.push(prob);
        }
      }
    }
    maxContributors = Math.max(maxContributors, probs.length);
    if (probs.length >= minContributors) {
      const p = median(probs);
      raw.push({ t, gap: false, prob: p, american: probabilityToAmerican(p), n: probs.length });
    } else {
      raw.push({ t, gap: true, reason: "insufficient" });
    }
  }

  const coalesced: AvgPoint[] = [];
  for (const point of raw) {
    const prev = coalesced[coalesced.length - 1];
    if (!prev || !statesEqual(prev, point)) {
      coalesced.push(point);
    }
  }

  return {
    status: "ok",
    points: decimate(coalesced, maxPoints),
    consensusCompleteFrom,
    maxContributors,
  };
}
